import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Alert,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import Header from "../../../../components/Header";
import {
  AssetFinanceUrl,
  customerservice,
  loanManagementUrl,
} from "../../../../config";
import {
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../../data/DynamicServiceNew/jsondynamicservice";

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return `KES ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// The API can return the error as a JSON array of plain business-readable
// reasons (e.g. ["Waiver amount ... exceeds remaining interest ... on loan ..."]),
// a plain string, or — for unexpected server errors — a JSON-stringified
// { Error: "Npgsql.PostgresException ...\n MessageText: <reason>\n..." }
// stack-trace envelope. Normalize all of these down to an array of readable lines.
const parseErrorMessages = (rawError) => {
  try {
    const parsed = typeof rawError === "string" ? JSON.parse(rawError) : rawError;
    if (Array.isArray(parsed)) return parsed.map(String);
    if (typeof parsed === "string") return [parsed];
    if (parsed?.Error) {
      const messageText = parsed.Error.match(/MessageText:\s*(.*?)(?:\r?\n|$)/);
      if (messageText?.[1]) return [messageText[1].trim()];
      const systemException = parsed.Error.match(/System\.Exception: (.*?)\./);
      if (systemException?.[1]) return [systemException[1]];
      return [parsed.Error];
    }
    if (parsed?.error) return [String(parsed.error)];
    return [JSON.stringify(parsed)];
  } catch {
    return [String(rawError)];
  }
};

const showErrorMessages = (rawError, title = "Error!") => {
  const messages = parseErrorMessages(rawError);
  if (messages.length > 1) {
    return Swal.fire({
      icon: "error",
      title,
      html: `<ul style="text-align:left; margin:0; padding-left:1.2em;">${messages
        .map((message) => `<li>${escapeHtml(message)}</li>`)
        .join("")}</ul>`,
    });
  }
  return Swal.fire({
    icon: "error",
    title,
    text: messages[0] || "An unknown error occurred.",
  });
};

const validationSchema = yup.object().shape({
  amount: yup
    .number()
    .typeError("Enter a valid amount")
    .positive("Amount must be greater than zero")
    .required("Amount is required"),
  narration: yup.string().nullable(),
});

const InterestWaiverForm = (props) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState(null);

  const [loans, setLoans] = useState([]);
  const [loadingLoans, setLoadingLoans] = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState("");

  useEffect(() => {
    if (!searchTerm || searchTerm.trim().length < 3) {
      setCustomer(null);
      return;
    }
    const delayDebounce = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await fetchJsonDynamic(
          "dynamicCustomerSearch",
          "customer",
          { searchTerm: searchTerm.trim(), approval: 2, isAssetCustomer: 1 },
          customerservice.uri,
        );
        const parsed = response?.results ? JSON.parse(response.results) : null;
        const found = Array.isArray(parsed) ? parsed[0] : parsed;
        if (found && found.Id) {
          setCustomer(found);
        } else {
          setCustomer(null);
        }
      } catch (error) {
        setCustomer(null);
      }
      setSearching(false);
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  useEffect(() => {
    const fetchLoans = async () => {
      setSelectedLoanId("");
      if (!customer?.Id) {
        setLoans([]);
        return;
      }
      setLoadingLoans(true);
      try {
        const response = await fetchJsonDynamic(
          "loanApplicationByCustomer",
          "loans",
          { customerId: Number(customer.Id), isLoanCleared: 0 },
          loanManagementUrl.uri,
        );
        const allLoans = response?.results ? JSON.parse(response.results) : [];
        const eligible = (Array.isArray(allLoans) ? allLoans : []).filter(
          (loan) => loan.LoanDisbursed === 1,
        );
        setLoans(eligible);

        if (eligible.length === 1) {
          setSelectedLoanId(eligible[0].Id);
        }
      } catch (error) {
        setLoans([]);
      } finally {
        setLoadingLoans(false);
      }
    };
    fetchLoans();
  }, [customer]);

  const selectedLoan = loans.find((loan) => loan.Id === selectedLoanId) || null;

  const resetCustomer = () => {
    setCustomer(null);
    setSearchTerm("");
    setLoans([]);
    setSelectedLoanId("");
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    if (!selectedLoanId) {
      Swal.fire(
        "Loan Required",
        "Please select the loan to waive interest on.",
        "warning",
      );
      setSubmitting(false);
      return;
    }
    try {
      const interestWaiver = {
        loanId: Number(selectedLoanId),
        amount: parseFloat(values.amount),
        waiverId: null,
        action: 0,
        narration: values.narration
          ? String(values.narration).replace(/\\/g, "\\\\").replace(/"/g, '\\"')
          : null,
      };

      const response = await executeAnyMutation(
        "insertInterestWaiver",
        interestWaiver,
        "interestWaiver",
        false,
        AssetFinanceUrl.uri,
      );

      if (response?.results) {
        props.onClose();
        props.onAction();
        resetForm();
        resetCustomer();
        await Swal.fire(
          "Success!",
          "Interest waiver has been staged and is now pending approval.",
          "success",
        );
      } else if (response?.error) {
        await showErrorMessages(response.error);
      }
    } catch (error) {
      await Swal.fire(
        "Error!",
        "An error occurred while processing your request. Please try again later.",
        "error",
      );
    }
    setSubmitting(false);
  };

  return (
    <Box m="20px">
      <Header
        title="New Interest Waiver"
        subtitle="Search for a customer, select their loan, then request an interest waiver for a checker to approve"
      />

      <TextField
        fullWidth
        variant="filled"
        label="Search Customer (Name, Phone or ID number)"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        disabled={!!customer}
        sx={{ mb: 2 }}
      />

      {searching && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={18} />
          <Typography variant="body2">Searching…</Typography>
        </Box>
      )}

      {!customer && !searching && searchTerm.trim().length >= 3 && (
        <Typography variant="body2" color="error" mb={2}>
          No approved customer found matching "{searchTerm}".
        </Typography>
      )}

      {customer && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={700}>
                  {customer.FirstName} {customer.OtherNames}
                </Typography>
                <Typography variant="body2">
                  Phone: {customer.MobileNumber}
                </Typography>
                <Typography variant="body2">
                  ID/Passport: {customer.IdNoOrPassport || "N/A"}
                </Typography>
              </Box>
              <IconButton
                onClick={resetCustomer}
                size="small"
                title="Search a different customer"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      )}

      {customer && loadingLoans && (
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <CircularProgress size={18} />
          <Typography variant="body2">Checking disbursed loans…</Typography>
        </Box>
      )}

      {customer && !loadingLoans && loans.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No disbursed loans found for this customer.
        </Alert>
      )}

      {customer && !loadingLoans && loans.length > 1 && (
        <FormControl fullWidth variant="filled" sx={{ mb: 2 }}>
          <InputLabel>Select Loan</InputLabel>
          <Select
            value={selectedLoanId}
            onChange={(e) => setSelectedLoanId(e.target.value)}
          >
            <MenuItem value="" disabled>
              Select Loan
            </MenuItem>
            {loans.map((loan) => (
              <MenuItem key={loan.Id} value={loan.Id}>
                Loan #{loan.Id}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {selectedLoan && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              Loan Details
            </Typography>
            <Box
              display="grid"
              gridTemplateColumns="repeat(auto-fit, minmax(140px, 1fr))"
              gap={2}
            >
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Loan ID
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedLoan.Id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Amount Disbursed
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatCurrency(selectedLoan.AmountToDisburse)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Loan Balance
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatCurrency(selectedLoan.LoanBalance)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Arrears Balance
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {formatCurrency(selectedLoan.Arrears)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Arrears Overdue Days
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedLoan.ArrearsOverDueDays || 0}
                </Typography>
              </Box>
             
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Expected Clear Date
                </Typography>
                <Typography variant="body1" fontWeight={600}>
                  {selectedLoan.ExpectedClearDate
                    ? new Date(
                        selectedLoan.ExpectedClearDate,
                      ).toLocaleDateString()
                    : "-"}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {customer && selectedLoanId && (
        <Formik
          initialValues={{ amount: "", narration: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box
                display="grid"
                gap="20px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              >
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  inputMode="numeric"
                  label="Waiver Amount (KES) *"
                  name="amount"
                  value={
                    values.amount === ""
                      ? ""
                      : Number(values.amount).toLocaleString()
                  }
                  onChange={(e) => {
                    const raw = e.target.value
                      .replace(/,/g, "")
                      .replace(/[^0-9]/g, "");
                    setFieldValue("amount", raw);
                  }}
                  onBlur={handleBlur}
                  error={touched.amount && !!errors.amount}
                  helperText={touched.amount && errors.amount}
                  sx={{ gridColumn: "span 2" }}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  label="Narration"
                  name="narration"
                  value={values.narration}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={{ gridColumn: "span 2" }}
                />
              </Box>
              <Box display="flex" justifyContent="end" mt="20px">
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Submitting..." : "Request Interest Waiver"}
                </Button>
              </Box>
            </form>
          )}
        </Formik>
      )}
    </Box>
  );
};

export default InterestWaiverForm;
